'use client';

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useConnectedAccounts } from '@/hooks/useConnectedAccounts';

/**
 * Phase 6 — configure optional Slack channel + Discord webhook for “publish digest” pings.
 */
export function WorkspacePublishNotifyCard() {
  const { data: accounts, isPending } = useConnectedAccounts(true);
  const slackConnected = Boolean(accounts?.slack?.isConnected);
  const [slackChannelId, setSlackChannelId] = useState('');
  const [discordWebhookUrl, setDiscordWebhookUrl] = useState('');
  const [channels, setChannels] = useState<Array<{ id: string; name: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setMessage(null);
    const r = await fetch('/api/user/workspace-publish-notify', { credentials: 'include' });
    if (!r.ok) return;
    const j = (await r.json()) as { slackChannelId?: string; discordWebhookUrl?: string };
    setSlackChannelId(j.slackChannelId ?? '');
    setDiscordWebhookUrl(j.discordWebhookUrl ?? '');
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!slackConnected) {
      setChannels([]);
      return;
    }
    let cancelled = false;
    void fetch('/api/slack/conversations', { credentials: 'include' })
      .then((r) => r.json())
      .then((j: { channels?: Array<{ id: string; name: string }> }) => {
        if (!cancelled && Array.isArray(j.channels)) setChannels(j.channels);
      })
      .catch(() => {
        if (!cancelled) setChannels([]);
      });
    return () => {
      cancelled = true;
    };
  }, [slackConnected]);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const r = await fetch('/api/user/workspace-publish-notify', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slackChannelId,
          discordWebhookUrl,
        }),
      });
      if (!r.ok) {
        const t = await r.text();
        setMessage(t || 'Save failed.');
        return;
      }
      setMessage('Saved.');
    } finally {
      setSaving(false);
    }
  };

  const testSlack = async () => {
    setMessage(null);
    const r = await fetch('/api/slack/test-notify', { method: 'POST', credentials: 'include' });
    const j = await r.json().catch(() => ({}));
    setMessage(r.ok ? 'Slack test sent.' : (j as { error?: string }).error || 'Slack test failed.');
  };

  const testDiscord = async () => {
    setMessage(null);
    const r = await fetch('/api/discord/test-notify', { method: 'POST', credentials: 'include' });
    const j = await r.json().catch(() => ({}));
    setMessage(r.ok ? 'Discord test sent.' : (j as { error?: string }).error || 'Discord test failed.');
  };

  if (isPending) {
    return null;
  }

  return (
    <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle2" gutterBottom>
        After publish — workspace notifications (optional)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        When native publish succeeds, FloAI can send a short digest to Slack and/or Discord. Uses only{' '}
        <strong>chat:write</strong>-class Slack scopes already used for Profile connect; Discord uses a channel webhook
        URL you paste (no bot token stored).
      </Typography>

      {slackConnected ? (
        channels.length > 0 ? (
          <FormControl fullWidth sx={{ mb: 2 }} size="small">
            <InputLabel id="flo-slack-ch-label">Slack channel</InputLabel>
            <Select
              labelId="flo-slack-ch-label"
              label="Slack channel"
              value={slackChannelId}
              onChange={(e) => setSlackChannelId(String(e.target.value))}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {channels.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <TextField
            label="Slack channel ID"
            value={slackChannelId}
            onChange={(e) => setSlackChannelId(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            helperText="e.g. C0123… from channel details, or pick from the list when channel listing loads."
          />
        )
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          Connect <strong>Slack</strong> above to choose a channel or enter a channel ID.
        </Alert>
      )}

      <TextField
        label="Discord incoming webhook URL"
        value={discordWebhookUrl}
        onChange={(e) => setDiscordWebhookUrl(e.target.value)}
        fullWidth
        size="small"
        sx={{ mb: 2 }}
        placeholder="https://discord.com/api/webhooks/…"
        helperText="Server Settings → Integrations → Webhooks → New Webhook → copy URL."
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button variant="contained" size="small" onClick={() => void save()} disabled={saving}>
          Save
        </Button>
        <Button variant="outlined" size="small" onClick={() => void testSlack()} disabled={!slackConnected}>
          Test Slack
        </Button>
        <Button variant="outlined" size="small" onClick={() => void testDiscord()} disabled={!discordWebhookUrl.trim()}>
          Test Discord
        </Button>
      </Box>
      {message ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {message}
        </Typography>
      ) : null}
    </Box>
  );
}
